import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { MessageCircle, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { formatLocalDate } from '../utils/date';

const REACTION_EMOJIS = ['🙏', '❤️', '🙌'];

/**
 * GroupPrayerComments — collapsible thread of encouragements/replies
 * on a single group prayer. Any group member can post, only the comment
 * author + prayer author + admins can delete. Members can also toggle
 * quick reactions (🙏 ❤️ 🙌) on each comment.
 *
 * Props:
 *   prayer                 — the prayer object (needs id + user_id)
 *   defaultOpen (bool)     — start expanded (default false, "N replies" collapsed)
 *   compact (bool)         — smaller styling for embedded panel usage
 */
const GroupPrayerComments = ({ prayer, defaultOpen = false, compact = false }) => {
  const { user, API } = useContext(AuthContext);
  const [open, setOpen] = useState(defaultOpen);
  const [comments, setComments] = useState(null); // null = not yet loaded
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [reactingKey, setReactingKey] = useState(null); // "commentId:emoji"

  const fetchComments = useCallback(async () => {
    if (!prayer?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/prayers/${prayer.id}/comments`);
      setComments(res.data || []);
    } catch (e) {
      // Silent — if user isn't a member we shouldn't be rendering this anyway
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [API, prayer?.id]);

  // Load once when opened
  useEffect(() => {
    if (open && comments === null) {
      fetchComments();
    }
  }, [open, comments, fetchComments]);

  const handlePost = async () => {
    const t = text.trim();
    if (!t) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API}/prayers/${prayer.id}/comments`, { text: t });
      setComments((prev) => [...(prev || []), res.data]);
      setText('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post reply');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (comment) => {
    if (!window.confirm('Delete this reply?')) return;
    setDeletingId(comment.id);
    try {
      await axios.delete(`${API}/prayers/${prayer.id}/comments/${comment.id}`);
      setComments((prev) => (prev || []).filter((c) => c.id !== comment.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete reply');
    } finally {
      setDeletingId(null);
    }
  };

  const handleReact = async (comment, emoji) => {
    const key = `${comment.id}:${emoji}`;
    if (reactingKey === key) return;
    setReactingKey(key);
    try {
      const res = await axios.post(
        `${API}/prayers/${prayer.id}/comments/${comment.id}/react`,
        { emoji }
      );
      setComments((prev) =>
        (prev || []).map((c) => (c.id === comment.id ? res.data : c))
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save reaction');
    } finally {
      setReactingKey(null);
    }
  };

  const count = comments?.length;
  const label =
    count === undefined || count === null
      ? 'Replies'
      : count === 0
      ? 'Reply'
      : `${count} ${count === 1 ? 'reply' : 'replies'}`;

  return (
    <div
      className={`mt-3 border-t border-slate-200 ${compact ? 'pt-2' : 'pt-3'}`}
      data-testid={`prayer-comments-${prayer.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left text-xs text-slate-600 hover:text-[#2B6B7F] transition-colors"
        data-testid={`comments-toggle-${prayer.id}`}
      >
        <span className="flex items-center gap-1.5">
          <MessageCircle className="w-3.5 h-3.5" />
          {label}
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div className="mt-2 space-y-2" data-testid={`comments-panel-${prayer.id}`}>
          {loading && comments === null ? (
            <p className="text-xs text-slate-500 italic">Loading replies…</p>
          ) : (
            (comments || []).map((c) => {
              const isAuthor = user && c.user_id === user.id;
              const isPrayerAuthor = user && prayer.user_id === user.id;
              const canDelete = isAuthor || isPrayerAuthor || (user && user.role === 'admin');
              return (
                <div
                  key={c.id}
                  className={`flex items-start justify-between gap-2 rounded-md ${
                    compact ? 'bg-white p-2' : 'bg-slate-50 p-2.5'
                  }`}
                  data-testid={`comment-${c.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs">
                      <span className="font-semibold text-slate-800">{c.user_name}</span>
                      <span className="text-slate-400 ml-1.5">
                        {formatLocalDate(c.created_at, { month: 'short', day: 'numeric' })}
                      </span>
                    </p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap mt-0.5 break-words">
                      {c.text}
                    </p>

                    {/* Reactions row */}
                    <div className="flex flex-wrap items-center gap-1 mt-1.5">
                      {REACTION_EMOJIS.map((emoji) => {
                        const reactedUsers = c.reactions?.[emoji] || [];
                        const count = reactedUsers.length;
                        const mine = user && reactedUsers.includes(user.id);
                        const busy = reactingKey === `${c.id}:${emoji}`;
                        return (
                          <button
                            key={emoji}
                            type="button"
                            disabled={busy}
                            onClick={() => handleReact(c, emoji)}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border transition-colors ${
                              mine
                                ? 'bg-[#2B6B7F]/10 border-[#2B6B7F]/40 text-[#2B6B7F]'
                                : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                            } ${busy ? 'opacity-60' : ''}`}
                            aria-pressed={mine}
                            data-testid={`comment-react-${c.id}-${emoji}`}
                            title={mine ? 'Remove reaction' : 'Add reaction'}
                          >
                            <span className="leading-none">{emoji}</span>
                            {count > 0 && <span className="leading-none">{count}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => handleDelete(c)}
                      disabled={deletingId === c.id}
                      className="text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                      data-testid={`comment-delete-${c.id}`}
                      title="Delete reply"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })
          )}

          <div className="flex items-end gap-2 pt-1">
            <Textarea
              placeholder="Send an encouragement…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={2}
              maxLength={1000}
              className="text-sm min-h-[52px] resize-none"
              data-testid={`comment-input-${prayer.id}`}
            />
            <Button
              size="sm"
              onClick={handlePost}
              disabled={posting || !text.trim()}
              className="bg-[#2B6B7F] flex-shrink-0"
              data-testid={`comment-post-${prayer.id}`}
            >
              {posting ? '…' : 'Post'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupPrayerComments;
